import { cleanStatus } from '@/helpers/text'
import { formatDate } from '@/helpers/times'
import { useBatchStore } from '@/stores/BatchStore'
import { useUserStore } from '@/stores/UserStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { ADMIN_DIVISION_TERMS } from '@/constants'

export const isValidEmail = (email) => {
  if (email) {
    return email.match(
      /^([\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+\.)*[\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+@((((([a-z0-9]{1}[a-z0-9\-]{0,62}[a-z0-9]{1})|[a-z])\.)+[a-z]{2,6})|(\d{1,3}\.){3}\d{1,3}(\:\d{1,5})?)$/i
    )
  } else {
    return false
  }
}

export const isValidPassword = (password) => {
  if (password) {
    return password.match(
      /^(?=.*[A-Z])(?=.*[\W])(?=.*[0-9])(?=.*[a-z]).{8,128}$/gm
    )
  } else {
    return false
  }
}

const returnTips = (content) => {
  return Object.keys(content).reduce((acc, key) => {
    acc.push(`${content[key]}`)

    return acc
  }, [])
}

export const tooltipContentForStateCell = (errors, suggestedEdits) => {
  let tipsErrors = []
  let tipsSuggestedEdits = []

  if (errors && Object.keys(errors).length) {
    tipsErrors = returnTips(errors)
  }
  if (suggestedEdits && Object.keys(suggestedEdits).length) {
    tipsSuggestedEdits = returnTips(suggestedEdits)
  }

  return [...tipsErrors, ...tipsSuggestedEdits]
}

export const tooltipContentPerEachField = (errors, suggestedEdits, field) => {
  let tips = []
  if (errors && Object.keys(errors).includes(field)) {
    return `${errors[field]}`
  }
  if (suggestedEdits && Object.keys(suggestedEdits).includes(field)) {
    return `${suggestedEdits[field]}`
  }

  return ''
}

export const setToLocalStorageByUserId = (data, key, userId) => {
  const savedValues = JSON.parse(localStorage.getItem(key)) || {}
  const updatedValues = JSON.stringify({ ...savedValues, [userId]: data })
  localStorage.setItem(key, updatedValues)
}

export const getFromLocalStorageByUserId = (key, userId) => {
  const savedValues = JSON.parse(localStorage.getItem(key)) || {}
  return savedValues[userId] || null
}

export const getImageUrl = (iconName) => {
  return new URL(`../assets/3d-assets/icons/${iconName}`, import.meta.url).href
}
export const getCellClassRules = (field) => {
  return {
    '!border !border-r-[1px] !border-red-500 !border-r-red-500 !border-l-red-500 !border-t-red-500 !border-b-red-500': (params) => {
      return params.data?.errors && params.data?.errors[field] && !params.data?.is_loading && params.column.colId !== 'title'
    },
    '!border !border-r !border-yellow-500 !border-r-yellow-500 !border-l-yellow-500 !border-t-yellow-500 !border-b-yellow-500': (params) => {
      return (params.data?.errors && !params.data?.errors[field] && params.data?.suggested_edits[field]) && !params.data?.is_loading && params.column.colId !== 'title'
    },
    '!text-gray-400 !pointer-events-none': (params) => {
      const batchStore = useBatchStore()

      return ['set'].includes(field) && (batchStore.selectedBatch?.lock_id || batchStore.selectedBatch?.locked_sets?.length)
    }
  }
}

export const isMobile = () => {
  return /Mobi|Android|iP(hone|od|ad)|IEMobile|BlackBerry|Opera Mini|Mobile|Silk/.test(navigator.userAgent)
}

export const retrieveLocalStorageCards = (user) => {
  if (user) {
    const dismissedCardDataJSON = localStorage.getItem(`dismissedCoaching${user.id}`);
    return dismissedCardDataJSON ? JSON.parse(dismissedCardDataJSON) : null;
  }
  return null
}

export const getNextPaymentTextContentForSidebar = (config = {}) => {
  const subscription = config.subscription
  const expirationDate = subscription?.expiration_date

  if (!expirationDate) return ''

  if (subscription?.complimentary) return ''

  if (subscription?.will_cancel) {
    return `Expires: ${formatDate(expirationDate)}`
  }

  const nextPayment = new Date(expirationDate)
  nextPayment.setDate(nextPayment.getDate() + 1)
  return `Next Payment: ${formatDate(nextPayment)}`
}

export const formatNumberWithCommas = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const getCharacterDescriptionLimit = (createdAt) => {
  if (new Date(createdAt) < new Date('2025-04-15T00:00:00.000Z')) {
    return 3500
  }

  return 2400
}

export const isAddressMissing = () => {
  const userStore = useUserStore()
  const { user } = userStore

  const { address_1, city, state, postal_code } = user
  return !address_1 || !city || !state || !postal_code
}

export const getPlanInfo = () => {
  const userStore = useUserStore()
  const billingStore = useBillingStore()

  const currentPlanIdx = userStore.user?.stripe_default_payment_method && billingStore.plansOptions ? billingStore.plansOptions.findIndex(item => item.id === billingStore.currentPlan.id) : null
  const currentPlan = currentPlanIdx !== null ? billingStore.plansOptions[currentPlanIdx] : null
  
  return {
    ...currentPlan,
  }
}

export const getAdminDivisionLabel = (countryCode) => {
  if (!countryCode) return 'State'
  
  for (const [term, countries] of Object.entries(ADMIN_DIVISION_TERMS)) {
    if (countries.includes(countryCode)) {
      return term
    }
  }
  
  return 'State'
}

export const getFlagEmoji = (isoCode) => {
  if (!isoCode || isoCode.length !== 2) return ''
  return isoCode
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
}
